import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiUser, FiClock, FiArrowLeft, FiArrowRight, FiShare2 } = FiIcons;

const BlogPost = () => {
  const { slug } = useParams();

  // In a real app, you'd fetch the post data based on the slug
  const post = {
    title: 'The Complete Guide to Microsoft Purview Implementation in 2024',
    author: 'Michael Rodriguez',
    date: '2024-01-15',
    readTime: '12 min read',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop',
    category: 'Implementation',
    content: `
      <h2>Introduction</h2>
      <p>Microsoft Purview has revolutionized how organizations approach data governance and compliance. In this comprehensive guide, we'll walk you through everything you need to know to successfully implement Microsoft Purview in your organization.</p>
      
      <h2>Why Microsoft Purview?</h2>
      <p>Before diving into implementation details, let's understand why Microsoft Purview has become the go-to solution for enterprise data governance:</p>
      <ul>
        <li><strong>Unified Data Governance:</strong> Single platform for all your data governance needs</li>
        <li><strong>AI-Powered Insights:</strong> Automated data discovery and classification</li>
        <li><strong>Compliance Ready:</strong> Built-in templates for GDPR, HIPAA, and other regulations</li>
        <li><strong>Seamless Integration:</strong> Native integration with Microsoft 365 and Azure</li>
      </ul>

      <h2>Pre-Implementation Planning</h2>
      <p>Success starts with proper planning. Here's what you need to do before you begin:</p>
      
      <h3>1. Assess Your Current State</h3>
      <p>Before implementing Purview, you need to understand your current data landscape:</p>
      <ul>
        <li>Inventory all data sources across your organization</li>
        <li>Identify sensitive data types and their locations</li>
        <li>Document current compliance requirements</li>
        <li>Assess existing data governance processes</li>
      </ul>

      <h3>2. Define Your Goals</h3>
      <p>Clear objectives are crucial for a successful implementation:</p>
      <ul>
        <li>Compliance requirements (GDPR, CCPA, HIPAA, etc.)</li>
        <li>Data security improvements</li>
        <li>Operational efficiency gains</li>
        <li>Risk reduction targets</li>
      </ul>

      <h2>Implementation Phases</h2>
      
      <h3>Phase 1: Foundation Setup (Week 1-2)</h3>
      <p>The first phase focuses on establishing the basic infrastructure:</p>
      <ul>
        <li>Set up Microsoft Purview account and permissions</li>
        <li>Configure basic data sources</li>
        <li>Establish governance framework</li>
        <li>Train initial admin team</li>
      </ul>

      <h3>Phase 2: Data Discovery and Classification (Week 3-4)</h3>
      <p>This phase involves discovering and classifying your data:</p>
      <ul>
        <li>Run automated data discovery scans</li>
        <li>Configure custom classification rules</li>
        <li>Review and validate automated classifications</li>
        <li>Set up data lineage tracking</li>
      </ul>
      
      <h3>Phase 3: Policy Implementation (Week 5-6)</h3>
      <p>Now we implement governance policies:</p>
      <ul>
        <li>Create data loss prevention policies</li>
        <li>Set up retention policies</li>
        <li>Configure access controls</li>
        <li>Implement compliance reporting</li>
      </ul>
      
      <h2>Best Practices for Success</h2>

      <h3>1. Start Small, Scale Gradually</h3>
      <p>Begin with a pilot project focusing on one department or data type. This allows you to:</p>
      <ul>
        <li>Learn the platform without overwhelming your team</li>
        <li>Identify and resolve issues early</li>
        <li>Build confidence and expertise</li>
        <li>Demonstrate value to stakeholders</li>
      </ul>

      <h3>2. Invest in Training</h3>
      <p>Your team's expertise is crucial for success:</p>
      <ul>
        <li>Provide comprehensive training for administrators</li>
        <li>Educate end-users on new processes</li>
        <li>Establish a center of excellence</li>
        <li>Keep up with Microsoft updates and new features</li>
      </ul>

      <h3>3. Maintain Data Quality</h3>
      <p>Good data governance starts with good data:</p>
      <ul>
        <li>Implement data quality checks</li>
        <li>Establish data stewardship roles</li>
        <li>Regular data audits and cleanup</li>
        <li>Monitor data lineage and usage</li>
      </ul>
      
      <h2>Common Pitfalls to Avoid</h2>

      <h3>1. Rushing the Implementation</h3>
      <p>Taking shortcuts during implementation often leads to:</p>
      <ul>
        <li>Incomplete data discovery</li>
        <li>Poorly configured policies</li>
        <li>User resistance due to lack of training</li>
        <li>Compliance gaps</li>
      </ul>

      <h3>2. Ignoring Change Management</h3>
      <p>Technical implementation is only half the battle. Don't forget:</p>
      <ul>
        <li>Communicating changes to all stakeholders</li>
        <li>Managing user adoption and resistance</li>
        <li>Updating existing processes and procedures</li>
        <li>Providing ongoing support and training</li>
      </ul>
      
      <h2>Measuring Success</h2>
      <p>Track these key metrics to measure your implementation success:</p>
      <ul>
        <li><strong>Data Discovery Rate:</strong> Percentage of data sources cataloged</li>
        <li><strong>Classification Accuracy:</strong> Accuracy of automated data classification</li>
        <li><strong>Policy Compliance:</strong> Adherence to governance policies</li>
        <li><strong>User Adoption:</strong> Number of active users and usage patterns</li>
        <li><strong>Risk Reduction:</strong> Decrease in compliance violations and security incidents</li>
      </ul>

      <h2>Next Steps</h2>
      <p>Ready to get started with your Microsoft Purview implementation? Here's what we recommend:</p>
      <ol>
        <li>Conduct a data governance assessment</li>
        <li>Define your implementation roadmap</li>
        <li>Assemble your project team</li>
        <li>Start with a pilot project</li>
        <li>Plan for ongoing support and optimization</li>
      </ol>

      <h2>Conclusion</h2>
      <p>Microsoft Purview implementation doesn't have to be complex or risky. With proper planning, the right expertise, and a phased approach, you can achieve data governance excellence and regulatory compliance.</p>
      <p>Remember, the key to success is not just implementing the technology, but transforming your organization's approach to data governance. Take the time to plan properly, invest in training, and focus on user adoption.</p>
    `
  };

  const relatedPosts = [
    {
      title: 'GDPR Compliance Made Easy with Microsoft Purview',
      slug: 'gdpr-compliance-microsoft-purview',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=300&h=200&fit=crop'
    },
    {
      title: '5 Common Microsoft Purview Implementation Mistakes to Avoid',
      slug: 'common-purview-implementation-mistakes-avoid',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=300&h=200&fit=crop'
    }
  ];

  return (
    <div className="py-16">
      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <Link
          to="/blog"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2" />
          Back to Blog
        </Link>
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="mb-6">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              {post.category}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center space-x-6 text-gray-600 mb-8">
            <div className="flex items-center">
              <SafeIcon icon={FiUser} className="mr-2" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center">
              <SafeIcon icon={FiCalendar} className="mr-2" />
              <span>{new Date(post.date).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center">
              <SafeIcon icon={FiClock} className="mr-2" />
              <span>{post.readTime}</span>
            </div>
            <button className="flex items-center text-blue-600 hover:text-blue-700">
              <SafeIcon icon={FiShare2} className="mr-2" />
              <span>Share</span>
            </button>
          </div>
          <img src={post.image} alt={post.title} className="w-full h-64 md:h-96 object-cover rounded-lg shadow-lg" />
        </motion.header>

        {/* Article Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <div
            dangerouslySetInnerHTML={{ __html: post.content }}
            className="prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-700 prose-p:leading-relaxed prose-li:text-gray-700 prose-strong:text-gray-900"
          />
        </motion.div>

        {/* Author Bio */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 p-6 bg-gray-50 rounded-lg"
        >
          <div className="flex items-start space-x-4">
            <img
              src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face"
              alt={post.author}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-1">{post.author}</h4>
              <p className="text-gray-600 mb-3">
                Founder & CEO of YourPurviewPartner. Former Microsoft Principal Architect with 15+ years in data governance. Led Purview implementations for Fortune 100 companies.
              </p>
              <div className="flex space-x-3 text-sm">
                <a href="#" className="text-blue-600 hover:text-blue-700">LinkedIn</a>
                <a href="#" className="text-blue-600 hover:text-blue-700">Twitter</a>
                <a href="#" className="text-blue-600 hover:text-blue-700">More Posts</a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 p-8 bg-blue-600 text-white rounded-lg text-center"
        >
          <h3 className="text-2xl font-bold mb-4">
            Ready to Implement Microsoft Purview?
          </h3>
          <p className="text-lg mb-6 text-blue-100">
            Get expert help with your Microsoft Purview implementation. Our team has successfully deployed Purview for 500+ organizations.
          </p>
          <Link
            to="/purview-audit"
            className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-bold transition-colors"
          >
            Get Your Purview Assessment
            <SafeIcon icon={FiArrowRight} className="ml-2" />
          </Link>
        </motion.div>
      </article>

      {/* Related Posts */}
      <section className="mt-16 bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Related Articles</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {relatedPosts.map((relatedPost, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <img src={relatedPost.image} alt={relatedPost.title} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    <Link
                      to={`/blog/${relatedPost.slug}`}
                      className="hover:text-blue-600 transition-colors"
                    >
                      {relatedPost.title}
                    </Link>
                  </h3>
                  <Link
                    to={`/blog/${relatedPost.slug}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Read More
                    <SafeIcon icon={FiArrowRight} className="ml-1" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;